import { Injectable } from '@angular/core';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  User,
} from 'firebase/auth';
import { environment } from 'src/environments/environment';

export interface SocialAuthResult {
  idToken: string;
  user: User;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private googleProvider: GoogleAuthProvider | null = null;
  private appleProvider: OAuthProvider | null = null;

  public isEnabled: boolean = false;

  constructor() {
    const isFlagOn = (environment as any).FIREBASE_ENABLED;
    this.isEnabled =
      isFlagOn === true ||
      isFlagOn === 'true' ||
      isFlagOn === 'True';

    if (this.isEnabled) {
      const config = (environment as any).firebaseConfig;
      if (config && config.apiKey) {
        if (!getApps().length) {
          this.app = initializeApp(config);
        } else {
          this.app = getApps()[0];
        }
        this.auth = getAuth(this.app);

        this.googleProvider = new GoogleAuthProvider();
        this.googleProvider.setCustomParameters({ prompt: 'select_account' });

        this.appleProvider = new OAuthProvider('apple.com');
        this.appleProvider.addScope('email');
        this.appleProvider.addScope('name');
      } else {
        this.isEnabled = false;
      }
    }
  }

  async signInWithGoogle(): Promise<SocialAuthResult> {
    if (!this.isEnabled || !this.auth || !this.googleProvider) {
      throw new Error('Firebase authentication is currently disabled.');
    }
    const result = await signInWithPopup(this.auth, this.googleProvider);
    const idToken = await result.user.getIdToken();
    return { idToken, user: result.user };
  }

  async signInWithApple(): Promise<SocialAuthResult> {
    if (!this.isEnabled || !this.auth || !this.appleProvider) {
      throw new Error('Firebase authentication is currently disabled.');
    }
    const result = await signInWithPopup(this.auth, this.appleProvider);
    const idToken = await result.user.getIdToken();
    return { idToken, user: result.user };
  }

  get currentUser(): User | null {
    return this.auth ? this.auth.currentUser : null;
  }
}
